import React, { FC, useEffect, useState, useRef, FormEvent, useMemo } from 'react';
import { Error } from 'components';
import classnames from 'classnames';
import { hexColorRegex, t, availableServiceColors } from 'utils';
import { ITextInput } from '../TextInput/TextInput';

import styles from './ColorSelect.module.scss';

interface IColorSelect extends ITextInput {
  defaultColor?: string;
}
interface CustomEventTarget extends EventTarget {
  value: string;
}
const ColorSelect: FC<IColorSelect> = ({
  name,
  label,
  error,
  value,
  active,
  touched,
  disabled,
  required,
  className,
  defaultColor = '#ffffff',
  labelVariable,
  errorClassname,
  placeholder = '',
  onBlur = () => {},
  onFocus = () => {},
  onChange = () => {},
  placeholderVariable,
}) => {
  const valueIsValidHexColor = useMemo(() => hexColorRegex.test(value), [value]);
  const colorToShow = useMemo((): string => (valueIsValidHexColor ? value : defaultColor), [
    value,
    defaultColor,
    valueIsValidHexColor,
  ]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [selectedColor, setSelectedColor] = useState(colorToShow || defaultColor);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  const rootClassName = classnames(styles['ta-color-input'], className, {
    [styles.focused]: active,
    [styles.disabled]: disabled,
    [styles.error]: error && touched,
    [styles.filled]: selectedColor && !active,
  });
  let labelText = '';
  if (label || labelVariable) {
    const textToReplace = labelVariable
      ? [
          {
            key: labelVariable.variable,
            value: labelVariable.value,
            translateReplace: labelVariable.translate,
          },
        ]
      : [];
    labelText = t(label, textToReplace);
  }
  let placeholderText;
  if (placeholder || placeholderVariable) {
    const textToReplace = placeholderVariable
      ? [
          {
            key: placeholderVariable.variable,
            value: placeholderVariable.value,
            translateReplace: placeholderVariable.translate,
          },
        ]
      : [];
    placeholderText = t(placeholder, textToReplace);
  }
  const handleColorChange = (color: string): void => {
    const eventObject: FormEvent<Element> = {
      nativeEvent: new Event('input'),
      currentTarget: document.createElement('input'),
      bubbles: true,
      cancelable: true,
      defaultPrevented: false,
      eventPhase: 0,
      isTrusted: true,
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      target: { value: color } as CustomEventTarget,
      type: 'change',
      timeStamp: Date.now(),
      preventDefault: () => {},
      stopPropagation: () => {},
      persist: () => {},
      isDefaultPrevented: () => false,
      isPropagationStopped: () => false,
    };
    setSelectedColor(color);
    onChange(eventObject);
    toggleDropdown();
  };
  const toggleDropdown = (): void => {
    setIsDropdownOpen(!isDropdownOpen);
  };
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSelectedColor(event.target.value);
    onChange(event);
  };

  return (
    <div className={rootClassName}>
      {labelText && (
        <label className={styles.label}>
          {labelText}
          {required && ' *'}
        </label>
      )}
      <input
        value={selectedColor.toUpperCase()}
        className={styles.input}
        name={name}
        type='text'
        required={required}
        onChange={handleInputChange}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        placeholder={placeholderText}
        autoComplete='off'
        onClick={toggleDropdown}
        ref={inputRef}
      />
      <div
        className={classnames(styles['ta-color-input__color-box'], {
          [styles['ta-color-input__color-box__white']]: ['#fff', '#ffffff'].includes(
            selectedColor.toLowerCase()
          ),
        })}
        style={{ backgroundColor: selectedColor }}
      />
      {isDropdownOpen && (
        <div className={styles.colorDropdown} ref={dropdownRef}>
          {availableServiceColors.map((color: string) => (
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <div
              key={color}
              className={styles.colorOption}
              onClick={() => handleColorChange(color)}
              style={{ backgroundColor: color }}
            ></div>
          ))}
        </div>
      )}
      <Error className={classnames('mt-1', errorClassname)} isError={error}>
        {error}
      </Error>
    </div>
  );
};

export default ColorSelect;
